/**
 * sales-models.ts — le référentiel des modèles de vente, partagé client et serveur.
 *
 * Sept manières de vendre, reprises de la banque de cas (spec/banque-de-cas.md §2), où elles
 * classent les cas socles. Une entreprise en pratique souvent plusieurs : un éditeur peut vendre
 * en direct aux PME ET par un réseau de partenaires.
 *
 * ⚠️ Les codes MV1..MV7 sont du vocabulaire interne : ils ne doivent JAMAIS s'afficher.
 * L'utilisateur lit « Vente complexe (grands comptes) », pas « MV3 ». Les libellés passent donc
 * par i18n (`salesModels.*`), jamais en dur dans le JSX.
 */

export const SALES_MODEL_CODES = ["MV1", "MV2", "MV3", "MV4", "MV5", "MV6", "MV7"] as const;
export type SalesModelCode = (typeof SALES_MODEL_CODES)[number];

/** Clé i18n du libellé et de la description de chaque modèle, dans l'ordre d'affichage. */
export const SALES_MODELS: Array<{ code: SalesModelCode; labelKey: string; hintKey: string }> = [
  { code: "MV1", labelKey: "salesModels.mv1", hintKey: "salesModels.mv1Hint" },
  { code: "MV2", labelKey: "salesModels.mv2", hintKey: "salesModels.mv2Hint" },
  { code: "MV3", labelKey: "salesModels.mv3", hintKey: "salesModels.mv3Hint" },
  { code: "MV4", labelKey: "salesModels.mv4", hintKey: "salesModels.mv4Hint" },
  { code: "MV5", labelKey: "salesModels.mv5", hintKey: "salesModels.mv5Hint" },
  { code: "MV6", labelKey: "salesModels.mv6", hintKey: "salesModels.mv6Hint" },
  { code: "MV7", labelKey: "salesModels.mv7", hintKey: "salesModels.mv7Hint" },
];

/**
 * Marché couvert par chaque modèle. Sert à filtrer les cas quand le compte a déclaré ses
 * modèles : seule la vente aux particuliers est B2C, la relation client se pratique des deux
 * côtés (un service client répond à des entreprises comme à des particuliers).
 */
const MARKETS_BY_MODEL: Record<SalesModelCode, Array<"B2B" | "B2C">> = {
  MV1: ["B2B"],
  MV2: ["B2B"],
  MV3: ["B2B"],
  MV4: ["B2B"],
  MV5: ["B2B"],
  MV6: ["B2C"],
  MV7: ["B2B", "B2C"],
};

/** N'accepte que des codes du référentiel — une valeur inconnue est ignorée, pas propagée. */
export function sanitizeSalesModels(values: unknown): SalesModelCode[] {
  if (!Array.isArray(values)) return [];
  const known = new Set<string>(SALES_MODEL_CODES);
  return [...new Set(values.filter((v): v is SalesModelCode => typeof v === "string" && known.has(v)))];
}

/**
 * Les marchés d'un compte, déduits de ses modèles déclarés.
 * Renvoie un tableau VIDE quand rien n'est déclaré : à l'appelant de ne alors rien filtrer,
 * plutôt que de tout masquer.
 */
export function marketsForSalesModels(models: readonly string[] | null | undefined): Array<"B2B" | "B2C"> {
  const clean = sanitizeSalesModels(models as unknown);
  if (clean.length === 0) return [];
  const out = new Set<"B2B" | "B2C">();
  for (const m of clean) for (const market of MARKETS_BY_MODEL[m]) out.add(market);
  return [...out];
}
